import styled, { keyframes } from 'styled-components';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const ContactSectionWrapper = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  position: relative;
  overflow: hidden;
  
  /* Animated background shapes */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(241, 102, 0, 0.03) 0%, transparent 50%);
    animation: ${pulse} 15s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(241, 102, 0, 0.5), transparent);
  }
  
  @media only screen and (max-width: 667px) {
    padding: 60px 0;
  }
  
  .sectionTitle {
    text-align: center;
    margin-bottom: 60px;
    position: relative;
    z-index: 1;
    
    h2 {
      font-size: 46px;
      font-weight: 300;
      color: #fff;
      margin-bottom: 20px;
      letter-spacing: -0.025em;
      line-height: 1.2;
      background: linear-gradient(90deg, #fff, #f16600, #fff);
      background-size: 200% auto;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: ${shimmer} 4s linear infinite;
      
      @media only screen and (max-width: 991px) {
        font-size: 40px;
      }
      @media only screen and (max-width: 767px) {
        font-size: 36px;
      }
      @media only screen and (max-width: 575px) {
        font-size: 32px;
      }
    }
    
    p {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.7);
      max-width: 600px;
      margin: 0 auto;
      line-height: 1.6;
    }
  }
  
  .contactRow {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 30px;
    position: relative;
    z-index: 1;
    
    @media only screen and (max-width: 991px) {
      flex-direction: column;
      align-items: center;
    }
  }
  
  .col {
    flex: 1;
    min-width: 280px;
    max-width: 350px;
    
    @media only screen and (max-width: 991px) {
      width: 100%;
      max-width: 400px;
    }
  }
  
  .contactCard, .calendarCard {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 40px 30px;
    height: 100%;
    text-align: center;
    transition: all 0.4s ease;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, transparent, #f16600, transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover {
      transform: translateY(-10px);
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(241, 102, 0, 0.3);
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3), 0 0 30px rgba(241, 102, 0, 0.1);
      
      &::before {
        opacity: 1;
      }
      
      .icon-wrapper {
        transform: scale(1.1);
        
        &::after {
          animation: ${ripple} 1s ease-out;
        }
      }
    }
    
    .icon-wrapper {
      width: 80px;
      height: 80px;
      margin: 0 auto 25px;
      background: linear-gradient(135deg, #f16600 0%, #ff8534 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.3s ease;
      position: relative;
      animation: ${float} 4s ease-in-out infinite;
      
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: rgba(241, 102, 0, 0.3);
      }
      
      svg {
        width: 36px;
        height: 36px;
        color: #fff;
        position: relative;
        z-index: 1;
        flex-shrink: 0;
      }
      
      @media only screen and (max-width: 667px) {
        width: 70px;
        height: 70px;
        
        svg {
          width: 30px;
          height: 30px;
        }
      }
    }
    
    h3 {
      font-size: 22px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 15px;
      
      @media only screen and (max-width: 667px) {
        font-size: 20px;
      }
    }
    
    .emailDescription, .calendarDescription {
      color: rgba(255, 255, 255, 0.6);
      font-size: 15px;
      margin-bottom: 25px;
      line-height: 1.7;
    }
    
    .emailLink {
      display: inline-block;
      font-size: 18px;
      color: #f16600;
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;
      padding: 10px 20px;
      border-radius: 8px;
      background: rgba(241, 102, 0, 0.1);
      
      &:hover {
        color: #fff;
        background: #f16600;
        transform: translateY(-2px);
      }
      
      @media only screen and (max-width: 667px) {
        font-size: 16px;
      }
    }
    
    .bookingButton {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(135deg, #f16600 0%, #ff8534 100%);
      color: #fff;
      font-size: 16px;
      font-weight: 600;
      padding: 16px 35px;
      border-radius: 8px;
      text-decoration: none;
      transition: all 0.3s ease;
      border: none;
      cursor: pointer;
      box-shadow: 0 10px 30px rgba(241, 102, 0, 0.3);
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(241, 102, 0, 0.4);
      }
      
      &:active {
        transform: translateY(-1px);
      }
      
      @media only screen and (max-width: 667px) {
        padding: 14px 28px;
        font-size: 14px;
      }
    }
  }
  
  /* Stagger the float animation for each card */
  .col:nth-child(1) .icon-wrapper {
    animation-delay: 0s;
  }
  .col:nth-child(2) .icon-wrapper {
    animation-delay: 0.5s;
  }
  .col:nth-child(3) .icon-wrapper {
    animation-delay: 1s;
  }
`;

export default ContactSectionWrapper;
